/*
  Inner Botanica – Doctor Visit Kit: content, lexicons, and rules.
  window.IB_DK_DATA is consumed by doctor-kit.js.

  CONTRACT: chip/option `id` values are encoded into shared ?k= links and are
  permanent once live. Labels, phrases, and copy may change freely; ids must
  never be renamed or reused. Bump `version` if the payload shape changes.

  Every user-facing string in this file is mirrored in docs/doctor-kit-STRINGS.md
  for compliance review. Free-text answers are never translated — they render
  verbatim; only chip selections pass through the lexicons below.
*/
window.IB_DK_DATA = (function () {
  "use strict";

  /* ---------------------------------------------------------------- S1 —
     Symptom checklist. `term` is the clinical-register phrase used on the
     sheet; `group` places non-prioritized symptoms under a neutral heading
     in "Additional Symptoms Reported". */
  var SYMPTOMS = [
    { id: "sleep_3am",        label: "Waking at 3am, mind racing",              term: "Sleep-maintenance insomnia (early-morning waking with racing thoughts)", group: "metabolic" },
    { id: "sleep_falling",    label: "Can't fall asleep",                        term: "Sleep-onset insomnia",                                                   group: "metabolic" },
    { id: "hot_flashes",      label: "Hot flashes",                              term: "Vasomotor symptoms — hot flushes",                                       group: "vasomotor" },
    { id: "night_sweats",     label: "Night sweats",                             term: "Nocturnal vasomotor symptoms — night sweats",                            group: "vasomotor" },
    { id: "rage",             label: "A short fuse that isn't like me",          term: "Irritability with low frustration tolerance (patient reports uncharacteristic)", group: "mood" },
    { id: "anxiety",          label: "Anxiety that came out of nowhere",         term: "New-onset or worsening anxiety",                                         group: "mood" },
    { id: "mood_swings",      label: "Mood swings / crying easily",              term: "Mood lability; episodes of tearfulness",                                 group: "mood" },
    { id: "brain_fog",        label: "Brain fog / losing words mid-sentence",    term: "Cognitive complaints — word-finding difficulty, reduced concentration",  group: "mood" },
    { id: "fatigue",          label: "Exhausted no matter how much I sleep",     term: "Persistent fatigue, unrelieved by adequate sleep",                       group: "metabolic" },
    { id: "weight_middle",    label: "Weight gain, especially the middle",       term: "Weight gain with central distribution",                                  group: "metabolic" },
    { id: "low_libido",       label: "Libido went missing",                      term: "Diminished libido",                                                      group: "genito" },
    { id: "irregular_periods",label: "Periods gone irregular",                   term: "Menstrual cycle irregularity (variable cycle length)",                   group: "vasomotor" },
    { id: "heavy_periods",    label: "Periods gone heavier",                     term: "Increased menstrual flow (heavier bleeding)",                            group: "vasomotor" },
    { id: "palpitations",     label: "Heart racing / pounding out of nowhere",   term: "Episodic palpitations",                                                  group: "vasomotor" },
    { id: "joint_aches",      label: "New aches in joints",                      term: "Multifocal arthralgia (new onset)",                                      group: "general" },
    { id: "headaches",        label: "More headaches / migraines",               term: "Increased headache frequency / migraine",                                group: "general" }
  ];

  /* Neutral clinical group headings for the sheet (§6.1.4). Render order fixed;
     empty groups are omitted. */
  var SHEET_GROUPS = [
    { id: "mood",      title: "Mood & cognitive" },
    { id: "vasomotor", title: "Menstrual & vasomotor" },
    { id: "metabolic", title: "Metabolic & sleep" },
    { id: "genito",    title: "Genitourinary & sexual health" },
    { id: "general",   title: "General" }
  ];

  /* ------------------------------------------------------- S3–S5 detail —
     chips per prioritized symptom. `phrase` is what the sheet prints. */
  var DETAIL = {
    dur: [
      { id: "few_wks", label: "A few weeks",          phrase: "a few weeks" },
      { id: "few_mos", label: "A few months",         phrase: "a few months" },
      { id: "6_12mo",  label: "6–12 months",     phrase: "6–12 months" },
      { id: "1_2y",    label: "1–2 years",       phrase: "1–2 years" },
      { id: "gt_2y",   label: "Longer than 2 years",  phrase: "more than 2 years" }
    ],
    freq: [
      { id: "daily",     label: "Daily",                  phrase: "daily" },
      { id: "most_days", label: "Most days",              phrase: "most days" },
      { id: "few_week",  label: "A few times a week",     phrase: "several times weekly" },
      { id: "few_month", label: "A few times a month",    phrase: "several times monthly" },
      { id: "waves",     label: "It comes in waves",      phrase: "episodic, in waves" }
    ],
    pat: [
      { id: "night",  label: "Worse at night",          phrase: "worse nocturnally" },
      { id: "cycle",  label: "Worse around my cycle",   phrase: "worse perimenstrually" },
      { id: "stress", label: "Worse under stress",      phrase: "worse under stress" },
      { id: "worse",  label: "Getting worse over time", phrase: "progressive" },
      { id: "none",   label: "No pattern I can see",    phrase: "" }
    ],
    cost: [
      { id: "sleep", label: "Sleep",                              phrase: "sleep" },
      { id: "work",  label: "Work / focus",                       phrase: "work and concentration" },
      { id: "rel",   label: "My relationships",                   phrase: "relationships" },
      { id: "fam",   label: "My patience with my kids/family",    phrase: "patience with family" },
      { id: "ex",    label: "Exercise / energy",                  phrase: "exercise and energy" },
      { id: "sex",   label: "Sex / intimacy",                     phrase: "sexual intimacy" },
      { id: "self",  label: "Just feeling like myself",           phrase: "overall sense of self" }
    ]
  };

  /* ------------------------------------------------------------------ S6 —
     What she's already tried. */
  var TRIED = {
    chips: [
      { id: "diet",       label: "Diet changes (keto / low-carb / etc.)", phrase: "dietary modification (low-carbohydrate)" },
      { id: "fasting",    label: "Intermittent fasting",                  phrase: "intermittent fasting" },
      { id: "exercise",   label: "Exercise changes",                      phrase: "structured exercise" },
      { id: "meditation", label: "Meditation / breathwork",               phrase: "meditation/breathwork" },
      { id: "sleep_hyg",  label: "Sleep hygiene (“all of it”)", phrase: "sleep hygiene protocol" },
      { id: "cut_alc",    label: "Cutting alcohol / caffeine",            phrase: "alcohol/caffeine reduction" },
      { id: "supps",      label: "Supplements",                           phrase: "OTC supplementation" },
      { id: "therapy",    label: "Therapy / counseling",                  phrase: "psychotherapy" },
      { id: "hrt",        label: "HRT (current or past)",                 phrase: "HRT (current or prior)" },
      { id: "bc",         label: "Birth control for symptoms",            phrase: "hormonal contraception for symptom management" },
      { id: "acu",        label: "Acupuncture / alternative",             phrase: "acupuncture / alternative modalities" },
      { id: "everything", label: "Honestly — everything I could find", phrase: "" }
    ],
    everythingPrefix: "Extensive self-directed efforts, including: ",
    outcomes: [
      { id: "helped_little", label: "Some things helped a little", phrase: "Patient reports partial, non-durable improvement." },
      { id: "nothing_stuck", label: "Nothing stuck",               phrase: "Patient reports partial, non-durable improvement." },
      { id: "no_difference", label: "Made no difference",          phrase: "No meaningful improvement." },
      { id: "made_worse",    label: "Some made it worse",          phrase: "Some approaches worsened symptoms." }
    ]
  };

  /* ------------------------------------------------------------------ S8 —
     Cycle status. phrase === "" means the sheet section is omitted. */
  var CYCLE = [
    { id: "regular",  label: "Regular, like always",                                             phrase: "Cycles regular." },
    { id: "rogue",    label: "Still coming, but the timing's gone rogue",                        phrase: "Cycles present with irregular timing." },
    { id: "skipping", label: "Skipping months",                                                  phrase: "Oligomenorrhea (skipped cycles)." },
    { id: "none_12",  label: "No period in over 12 months",                                      phrase: "No menses > 12 months." },
    { id: "na",       label: "No period — hysterectomy / ablation / IUD makes it hard to tell", phrase: "Menstrual status not assessable (hysterectomy/ablation/IUD)." },
    { id: "skip",     label: "Prefer to skip",                                                   phrase: "" }
  ];

  /* ------------------------------------------------------------------ S9 —
     Appointment goal. */
  var GOAL = [
    { id: "serious",     label: "To be taken seriously — finally" },
    { id: "explanation", label: "A real explanation for what's happening" },
    { id: "options",     label: "To talk options — including or beyond hormones" },
    { id: "tests",       label: "Specific tests, so we're not guessing" },
    { id: "plan",        label: "A plan, not a “wait and see”" }
  ];

  /* --------------------------------------------------------- Deep branch — */
  var DEEP_SWEEP = [
    {
      id: "mood", label: "Mood & mind", items: [
        { id: "irritability", label: "Irritability",        term: "Irritability",              group: "mood" },
        { id: "low_mood",     label: "Low mood",            term: "Low mood",                  group: "mood" },
        { id: "panic",        label: "Panic moments",       term: "Episodic panic symptoms",   group: "mood" },
        { id: "memory",       label: "Memory slips",        term: "Subjective memory lapses",  group: "mood" }
      ]
    },
    {
      id: "cycle", label: "Cycle & body heat", items: [
        { id: "flooding",      label: "Flooding / clots",       term: "Menorrhagia with clots", group: "vasomotor" },
        { id: "breast_tender", label: "Breast tenderness",      term: "Mastalgia",              group: "vasomotor" },
        { id: "chills",        label: "Chills after flashes",   term: "Post-flush chills",      group: "vasomotor" }
      ]
    },
    {
      id: "metab", label: "Metabolism & sleep", items: [
        { id: "sugar_crave",   label: "Sugar cravings",       term: "Increased carbohydrate cravings",          group: "metabolic" },
        { id: "pm_crash",      label: "Afternoon crashes",    term: "Post-prandial/afternoon energy crashes",   group: "metabolic" },
        { id: "snoring",       label: "Snoring (new)",        term: "New-onset snoring (partner-reported)",     group: "metabolic" },
        { id: "restless_legs", label: "Restless legs",        term: "Restless legs symptoms",                   group: "metabolic" }
      ]
    },
    {
      id: "intimacy", label: "Intimacy & tissue", items: [
        { id: "dryness",      label: "Vaginal dryness",              term: "Vaginal dryness",                     group: "genito" },
        { id: "dyspareunia",  label: "Pain with sex",                term: "Dyspareunia",                         group: "genito" },
        { id: "urgency",      label: "Urinary urgency / frequency",  term: "Urinary urgency/frequency",           group: "genito" },
        { id: "utis",         label: "Recurrent UTIs",               term: "Recurrent urinary tract infections",  group: "genito" }
      ]
    }
  ];

  var DEEP_SLEEP = {
    hrs: [
      { id: "lt5",  label: "Under 5 hours", phrase: "under 5 h/night" },
      { id: "5_6",  label: "5–6 hours", phrase: "~5–6 h/night" },
      { id: "6_7",  label: "6–7 hours", phrase: "~6–7 h/night" },
      { id: "7up",  label: "7 or more",      phrase: "7+ h/night" }
    ],
    wakes: [
      { id: "0",   label: "0",  phrase: "" },
      { id: "1",   label: "1",  phrase: "wakes 1×/night" },
      { id: "2",   label: "2",  phrase: "wakes 2×/night" },
      { id: "3up", label: "3+", phrase: "wakes 3+×/night" }
    ],
    when: [
      { id: "before2", label: "Before 2am", phrase: "typically before 2am" },
      { id: "2_4",     label: "2–4am", phrase: "typically 2–4am" },
      { id: "after4",  label: "After 4am",  phrase: "typically after 4am" },
      { id: "varies",  label: "Varies",     phrase: "" }
    ]
  };

  var DEEP_WT = {
    chg: [
      { id: "none",  label: "None",            phrase: "" },
      { id: "5",     label: "~5 lb",           phrase: "~5 lb gain over 12 months" },
      { id: "5_15",  label: "5–15 lb",    phrase: "5–15 lb gain over 12 months" },
      { id: "15up",  label: "15+ lb",          phrase: "15+ lb gain over 12 months" },
      { id: "lost",  label: "Lost weight",     phrase: "weight loss over 12 months" }
    ],
    habits: [
      { id: "yes",  label: "Yes — I've changed my habits",          phrase: "despite changed habits" },
      { id: "no",   label: "No",                                          phrase: "" },
      { id: "same", label: "Habits are the same — that's the point", phrase: "with unchanged habits" }
    ]
  };

  var FAMILY = [
    { id: "early_meno",   label: "Early menopause (mother/sister)", term: "Early menopause (mother/sister)" },
    { id: "osteoporosis", label: "Osteoporosis",                    term: "Osteoporosis" },
    { id: "heart",        label: "Heart disease",                   term: "Heart disease" },
    { id: "breast_ca",    label: "Breast cancer",                   term: "Breast cancer" },
    { id: "thyroid",      label: "Thyroid conditions",              term: "Thyroid conditions" },
    { id: "none",         label: "None / unsure",                   term: "" }
  ];

  var PRIOR = [
    { id: "real",    label: "Yes — and got a real conversation" },
    { id: "brushed", label: "Yes — got brushed off" },
    { id: "stress",  label: "Yes — was told it's stress / normal / age" },
    { id: "first",   label: "No — this is the first time" }
  ];

  /* --------------------------------------------------- Screen definitions —
     Rendered by generic renderers in doctor-kit.js, in array order.
     S3–S5 are generated from `pri` at runtime via the single `detail` def. */
  var SCREENS = [
    {
      id: "s1", type: "chips", key: "sym",
      prompt: "What's been showing up?",
      sub: "Tap everything that's true — even the ones you've been told are “just stress.”",
      chips: SYMPTOMS, min: 1,
      other: { key: "symO", label: "Something else…", placeholder: "In your own words", maxlen: 200 }
    },
    {
      id: "s2", type: "rank", key: "pri",
      prompt: "Which three are costing you the most right now?",
      sub: "These will lead your sheet — and your appointment.",
      max: 3
    },
    {
      id: "detail", type: "detail", key: "det",
      prompt: "Let's get specific about: ",
      sub: "Specific is what gets taken seriously.",
      parts: [
        { key: "dur",  label: "How long?",                   chips: DETAIL.dur,  multi: false },
        { key: "freq", label: "How often?",                  chips: DETAIL.freq, multi: false },
        { key: "pat",  label: "Any pattern you've noticed?", chips: DETAIL.pat,  multi: true },
        { key: "cost", label: "What's it costing you?",      chips: DETAIL.cost, multi: true }
      ],
      free: { key: "txt", label: "In your own words, if you want:", maxlen: 200 }
    },
    {
      id: "s6", type: "chips", key: "tried",
      prompt: "What have you already tried?",
      sub: "This section quietly answers “have you considered exercise?” before anyone asks it.",
      chips: TRIED.chips, min: 0,
      other: { key: "o", label: "Other…", placeholder: "What else have you tried?", maxlen: 200 },
      followup: { key: "out", label: "And overall?", options: TRIED.outcomes }
    },
    {
      id: "s7", type: "text", key: "meds",
      prompt: "Anything you take regularly?",
      sub: "Doctors always ask — having it written down is half the credibility.",
      placeholder: "Medications, supplements, doses if you know them…",
      maxlen: 300,
      skipLabel: "Nothing regular"
    },
    {
      id: "s8", type: "single", key: "cyc",
      prompt: "Where are your cycles at?",
      sub: "This one question reframes everything else on the sheet.",
      options: CYCLE
    },
    {
      id: "s9", type: "single", key: "goal",
      prompt: "Last one. When you walk out of that appointment, what do you most want to have happened?",
      sub: "",
      options: GOAL,
      free: { key: "t", label: "Anything to add?", maxlen: 200 }
    },
    {
      id: "s10", type: "fork",
      prompt: "That's the core — your sheet is ready.",
      sub: "Want to go one layer deeper? Six more questions make it thorough enough that nothing gets waved off. Totally optional.",
      primary: "Show me my sheet",
      secondary: "Go deeper (2 more minutes)"
    },
    {
      id: "d1", type: "chips", key: "sweep", deep: true,
      prompt: "A quick full-body sweep.",
      sub: "Tap anything else that's been true lately.",
      groups: DEEP_SWEEP, min: 0
    },
    {
      id: "d2", type: "multi", key: "sleep", deep: true,
      prompt: "Your sleep, specifically.",
      sub: "",
      parts: [
        { key: "hrs",   label: "Typical hours a night?",             chips: DEEP_SLEEP.hrs },
        { key: "wakes", label: "How many times do you wake?",        chips: DEEP_SLEEP.wakes },
        { key: "when",  label: "If there's a consistent wake time:", chips: DEEP_SLEEP.when }
      ]
    },
    {
      id: "d3", type: "multi", key: "wt", deep: true,
      prompt: "Weight and metabolism, roughly.",
      sub: "",
      parts: [
        { key: "chg",    label: "Change over the last 12 months?", chips: DEEP_WT.chg },
        { key: "habits", label: "With changed habits?",            chips: DEEP_WT.habits }
      ]
    },
    {
      id: "d4", type: "chips", key: "fam", deep: true,
      prompt: "Any of this in the family?",
      sub: "Family history changes what's worth screening for.",
      chips: FAMILY, min: 0
    },
    {
      id: "d5", type: "single", key: "prior", deep: true,
      prompt: "Have you raised this with a doctor before?",
      sub: "",
      options: PRIOR
    },
    {
      id: "d6", type: "text", key: "note", deep: true,
      prompt: "Anything you want on the page that we didn't ask?",
      sub: "Your sheet, your words.",
      placeholder: "Anything else your doctor should see…",
      maxlen: 300,
      skipLabel: "Nothing to add"
    }
  ];

  /* ------------------------------------------------ Questions engine (§6.5) —
     Rules evaluated in priority order; first 4 that match win; the default
     always takes the last slot if one remains. If fewer than 3 match, pad
     from PAD order. All output is first-person, printed verbatim. */
  function priHas(a, ids) {
    return (a.pri || []).some(function (p) { return ids.indexOf(p) !== -1; });
  }
  var QUESTIONS = [
    {
      id: "q_peri",
      when: function (a) { return (a.cyc === "rogue" || a.cyc === "skipping") && (a.pri || []).length >= 2; },
      text: function () { return "Given the timing of these symptoms alongside my cycle changes, could this be perimenopause — and how would we assess that?"; }
    },
    {
      id: "q_serious",
      when: function (a) {
        var prior = a.deep && a.deep.prior;
        return (a.goal && a.goal.c === "serious") || prior === "brushed" || prior === "stress";
      },
      text: function () { return "If these symptoms don't fit a clear diagnosis today, what's our plan to keep investigating rather than wait and see?"; }
    },
    {
      id: "q_tests",
      when: function (a) { return a.goal && a.goal.c === "tests"; },
      text: function () { return "Which labs or tests would actually be informative at my stage — and which would you skip, and why?"; }
    },
    {
      id: "q_options",
      when: function (a) {
        return (a.goal && a.goal.c === "options") || (a.tried && (a.tried.c || []).indexOf("hrt") !== -1);
      },
      text: function () { return "What are my options — hormonal and non-hormonal — and what do you see as the tradeoffs for someone with my history?"; }
    },
    {
      id: "q_sleep",
      when: function (a) { return priHas(a, ["sleep_3am", "sleep_falling"]); },
      text: function () { return "What can we do about the sleep specifically? It's driving several of the others."; }
    },
    {
      id: "q_metab",
      when: function (a) { return priHas(a, ["weight_middle"]); },
      text: function () { return "Could metabolic changes be part of this picture, and is that worth testing?"; }
    },
    {
      id: "q_family",
      when: function (a) {
        var fam = (a.deep && a.deep.fam) || [];
        return fam.filter(function (f) { return f !== "none"; }).length > 0;
      },
      text: function (a) {
        var terms = ((a.deep && a.deep.fam) || [])
          .map(function (id) { var m = FAMILY.filter(function (f) { return f.id === id; })[0]; return m ? m.term : ""; })
          .filter(Boolean)
          .map(function (t) { return t.toLowerCase(); });
        return "Given my family history of " + terms.join(", ") + ", does that change what we screen for now?";
      }
    },
    {
      id: "q_track", isDefault: true,
      when: function () { return true; },
      text: function () { return "What should I track between now and a follow-up, so our next conversation is more concrete?"; }
    }
  ];
  /* If rules yield fewer than 3, pad in this order (broadly applicable). */
  var QUESTION_PAD = ["q_serious", "q_tests", "q_options"];

  /* --------------------------------------------------- Product routing (§8) —
     The ONLY product logic in the experience. Consumed exclusively by the
     post-download screen. Territory maps are flagged for review in STRINGS.md. */
  var PRODUCTS = {
    stabilityTerritory: ["sleep_3am", "sleep_falling", "rage", "anxiety", "mood_swings", "brain_fog", "fatigue"],
    awakenTrigger: "low_libido", /* only when prioritized #1 */
    cards: {
      stability: {
        body: "One more thing, only because it's true: the 3am wide-awake part — the racing mind, the short fuse, the fog — that's the territory we built Stability for. It's non-hormonal and works alongside whatever you and your doctor choose, including HRT.",
        productName: "Stability",
        link: "See what's in it →",
        urlKey: "stabilityUrl"
      },
      awaken: {
        body: "One more thing, only because it's true: the part where desire went quiet — that's the territory we built Awaken for. It's non-hormonal and works alongside whatever you and your doctor choose, including HRT.",
        productName: "Awaken",
        link: "See what's in it →",
        urlKey: "awakenUrl"
      }
    },
    fdaDisclaimer: "These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease."
  };

  /* -------------------------------------------------------------- Strings — */
  var STRINGS = {
    back: "Back",
    next: "Next",
    continueBtn: "Continue",
    detailHeaderPrefix: "Let's get specific about: ",
    rankHint: "Tap up to three, in order. Tap again to remove.",
    resumePrompt: "Pick up where you left off?",
    resumeYes: "Continue my sheet",
    resumeNo: "Start over",
    transition: "Putting your words in order…",
    sheet: {
      title: "Patient Preparation Sheet",
      preparedPrefix: "Prepared ",
      nameField: "Name:",
      dobField: "DOB:",
      topConcerns: "Top Concerns",
      symptomDetail: "Symptom Detail",
      additional: "Additional Symptoms Reported",
      menstrual: "Menstrual Status",
      tried: "Approaches Already Tried",
      meds: "Current Medications & Supplements",
      medsNone: "None regular.",
      family: "Relevant Family History",
      questions: "Patient's Questions for This Appointment",
      inHerWords: "In the patient's words: ",
      patientNote: "Patient note: ",
      footerCredit: "Prepared by the patient using the Feminine Shift™ intake · Inner Botanica · innerbotanica.com",
      footerDisclaimer: "This document organizes information self-reported by the patient. It is not a diagnosis, a medical record, or medical advice."
    },
    reveal: {
      truncationNote: "Heads up: to keep everything on one page, we shortened your longest note with an ellipsis. Everything else is untouched."
    },
    share: {
      copied: "Link copied. Text or email it to yourself — opening it on any device rebuilds your sheet.",
      copyFail: "Press and hold to copy your link:",
      shareTitle: "My Doctor Visit Sheet"
    },
    download: {
      iosHint: "If the PDF opened in a new tab: tap the share icon, then “Save to Files.”",
      blocked: "This in-app browser won't save files. Tap “Email / text it to myself” and open the link in Safari or Chrome — or use “or print this page.”"
    },
    after: {
      head: "You're walking in prepared. That's not a small thing.",
      body: "Most women sit in that room hoping to be believed. You're bringing the evidence. Whatever you and your doctor decide together — including hormones, if that's your path — we're for it.",
      friend: "Build a sheet for a friend",
      downloadAgain: "Download it again"
    }
  };

  return {
    version: 1,
    symptoms: SYMPTOMS,
    sheetGroups: SHEET_GROUPS,
    detail: DETAIL,
    tried: TRIED,
    cycle: CYCLE,
    goal: GOAL,
    deepSweep: DEEP_SWEEP,
    deepSleep: DEEP_SLEEP,
    deepWt: DEEP_WT,
    family: FAMILY,
    prior: PRIOR,
    screens: SCREENS,
    questions: QUESTIONS,
    questionPad: QUESTION_PAD,
    products: PRODUCTS,
    strings: STRINGS
  };
})();
